<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { Building } from "@types"
import type { Services } from "@services"
import { useBuildingStore } from "@core/stores/building-v2.store"
import type { PvFormPayload, PvSegment } from "./pv-form.types"
import { usePvStore } from "@modules/pv/stores/pv.store"
import PVFormContainer from "./PVFormContainer.vue"
import PVFormFinish from "./PVFormFinish.vue"
import PVFormGeneralInfo from "./PVFormGeneralInfo.vue"
import PVFormRoof from "./PVFormRoof.vue"

// ================================
// TYPES & INTERFACES
// ================================
type PvStep = "general" | "roof" | "finish"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        form?: PvFormPayload | null
        idSource?: string | null
        idBuilding?: string | null
        isOpen?: boolean
        idExtrapolation?: string | null
        initialStep?: PvStep
    }>(),
    {
        form: null,
        idSource: null,
        idBuilding: null,
        isOpen: false,
        idExtrapolation: null,
        initialStep: "general",
    }
)

const emit = defineEmits<{
    (event: "click:close"): void
    (event: "create:analyse", payload: PvFormPayload): void
    (event: "update:analyse", payload: PvFormPayload): void
}>()

// ================================
// STORES & SERVICES
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            pvDim: {
                timeline: {
                    general: "Info. générales",
                    roof: "Toitures et installations",
                },
            },
        },
    },
})
const pvStore = usePvStore()
const buildingStore = useBuildingStore()
const services = inject<Services>("services")

// ================================
// ETAT FORMULAIRE & GOOGLE SOLAR
// ================================
const step = ref<PvStep>(props.initialStep)
const formState = ref<PvFormPayload>({
    name: props.form?.name ?? "",
    extrapolation: props.form?.extrapolation ?? false,
    listParamsPv: props.form?.listParamsPv ?? [],
    _id_extrapolation: props.form?._id_extrapolation ?? null,
})

const googleSolarData = ref<Record<string, unknown> | null>(null)
const isLoadingGoogleSolar = ref(false)
const noCadastralPlotSelected = ref(false)

/**
 * Indique si le bâtiment a une parcelle cadastrale (requise pour interroger Google Solar).
 * @param building - Fiche bâtiment courante
 * @returns true si un numéro de parcelle est renseigné
 */
function getHasCadastralParcel(building: Building | null | undefined): boolean {
    const raw = building as
        | { infos?: { cadastre?: { numero_parcelle?: string } } }
        | null
        | undefined
    const n = raw?.infos?.cadastre?.numero_parcelle
    return typeof n === "string" && n.length > 0
}

/**
 * Demande les données Google Solar pour le bâtiment (création et modification, parcelle requise).
 * @returns void
 */
async function loadGoogleSolarData(): Promise<void> {
    if (!props.idBuilding) {
        return
    }
    if (!getHasCadastralParcel(buildingStore.building)) {
        noCadastralPlotSelected.value = true
        googleSolarData.value = null
        return
    }
    noCadastralPlotSelected.value = false
    if (services?.$getGoogleSolarData == null) {
        googleSolarData.value = null
        return
    }
    isLoadingGoogleSolar.value = true
    try {
        const data = await services.$getGoogleSolarData(props.idBuilding)
        googleSolarData.value = data as Record<string, unknown>
    } catch {
        googleSolarData.value = null
    } finally {
        isLoadingGoogleSolar.value = false
    }
}

// ================================
// COMPUTED
// ================================
const getTimelineSteps = computed(() => {
    if (step.value === "finish") {
        return []
    }
    if (step.value === "general") {
        return [
            { label: t("pvDim.timeline.general"), status: "current" as const },
            { label: t("pvDim.timeline.roof"), status: "disabled" as const },
        ]
    }
    return [
        {
            label: t("pvDim.timeline.general"),
            status: "completed" as const,
        },
        { label: t("pvDim.timeline.roof"), status: "current" as const },
    ]
})

// ================================
// INTERACTIONS
// ================================
function onSubmitGeneralInfo(payload: {
    name: string
    extrapolation: boolean
}): void {
    formState.value.name = payload.name
    formState.value.extrapolation = payload.extrapolation
    step.value = "roof"
}

/**
 * Enregistre les pans puis crée ou met à jour l’analyse (ex-étape validation).
 * @param payload - Liste des segments PV issus de l’étape toiture
 */
function onSubmitValidation(payload: { listParamsPv: PvSegment[] }): void {
    formState.value.listParamsPv = payload.listParamsPv

    if (formState.value.extrapolation && props.idExtrapolation) {
        formState.value._id_extrapolation = props.idExtrapolation
    } else {
        formState.value._id_extrapolation = null
    }

    pvStore.setRoofSegments(payload.listParamsPv)
    pvStore.setLastPvFormPayload(formState.value)

    if (props.form == null) {
        emit("create:analyse", { ...formState.value })
    } else {
        emit("update:analyse", { ...formState.value })
    }

    step.value = "finish"
}

function onBackToGeneral(): void {
    step.value = "general"
}

function onCloseFinish(): void {
    emit("click:close")
    step.value = "general"
}

/**
 * Remet le formulaire à l’ouverture du dialogue en mode création.
 * @returns void
 */
function resetFormStateForCreate(): void {
    formState.value = {
        name: "",
        extrapolation: false,
        listParamsPv: [],
        _id_extrapolation: null,
    }
    step.value = props.initialStep
    googleSolarData.value = null
    noCadastralPlotSelected.value = false
    void loadGoogleSolarData()
}

watch(
    () => props.form,
    (f) => {
        if (f) {
            formState.value = {
                name: f.name,
                extrapolation: f.extrapolation,
                listParamsPv: f.listParamsPv ? [...f.listParamsPv] : [],
                _id_extrapolation: f._id_extrapolation ?? null,
            }
        }
    },
    { deep: true }
)

watch(
    () => props.isOpen,
    (open) => {
        if (!open) return
        if (!props.form) {
            resetFormStateForCreate()
        }
    }
)

watch(
    () => [props.isOpen, props.idBuilding] as const,
    ([open, idBuilding]) => {
        if (open && idBuilding) {
            void loadGoogleSolarData()
        }
    },
    { immediate: true }
)
</script>

<template>
    <section class="pv-form flex w-full flex-col gap-6">
        <PVFormContainer
            :timeline="getTimelineSteps"
            :hide-timeline="step === 'finish'"
        >
            <PVFormGeneralInfo
                v-if="step === 'general'"
                :form-name="formState.name"
                :form-extrapolation="formState.extrapolation"
                :id-extrapolation="idExtrapolation"
                :is-modification="form != null"
                @click:submit="onSubmitGeneralInfo"
            />

            <PVFormRoof
                v-else-if="step === 'roof'"
                :id-building="idBuilding || undefined"
                :list-params-pv="formState.listParamsPv"
                :google-solar="googleSolarData"
                :loading="isLoadingGoogleSolar"
                :no-cadastral-plot-selected="noCadastralPlotSelected"
                @click:previous="onBackToGeneral"
                @click:submit="onSubmitValidation"
            />

            <PVFormFinish
                v-else
                :failure="false"
                @click:close="onCloseFinish"
            />
        </PVFormContainer>
    </section>
</template>
